import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  IconButton,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon, Help as HelpIcon, Visibility as VisibilityIcon, Edit as EditIcon } from '@mui/icons-material';
import { marked } from 'marked';

const AddQuestionDialog = ({ open, onClose, onAdd, topic }) => {
  const [formData, setFormData] = useState({
    questionText: '',
    answerText: '',
  });
  const [error, setError] = useState('');
  const [previewMode, setPreviewMode] = useState('edit'); // 'edit' or 'preview'

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    if (!formData.questionText.trim()) {
      setError('Question text is required');
      return;
    }

    try {
      await onAdd(formData);
      handleClose();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleClose = () => {
    setFormData({
      questionText: '',
      answerText: '',
    });
    setError('');
    setPreviewMode('edit');
    onClose();
  };

  const renderMarkdownPreview = () => {
    const htmlContent = marked(formData.answerText);
    return (
      <Box
        className="markdown-content"
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          minHeight: '200px',
          overflow: 'auto'
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'secondary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <HelpIcon sx={{ fontSize: 24 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Add New Question
            </Typography>
            {topic && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  to topic:
                </Typography>
                <Chip 
                  label={topic.title} 
                  size="small" 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 600
                  }} 
                />
              </Box>
            )}
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          Add a new question to help you practice and reinforce your understanding of this topic. Include both the question and your answer for effective learning. You can use Markdown formatting in the answer field.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="Question"
            name="questionText"
            value={formData.questionText}
            onChange={handleChange}
            required
            autoFocus
            multiline
            rows={4}
            placeholder="Enter your question here. Be specific and clear about what you want to learn or practice..."
            helperText="This is the main question you want to practice or learn about"
          />

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Answer (Optional)
              </Typography>
              <ToggleButtonGroup
                value={previewMode}
                exclusive
                onChange={(e, newMode) => newMode && setPreviewMode(newMode)}
                size="small"
              >
                <ToggleButton value="edit" sx={{ px: 2, py: 0.5 }}>
                  <EditIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  Edit
                </ToggleButton>
                <ToggleButton value="preview" sx={{ px: 2, py: 0.5 }}>
                  <VisibilityIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  Preview
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {previewMode === 'edit' ? (
              <TextField
                fullWidth
                label="Answer"
                name="answerText"
                value={formData.answerText}
                onChange={handleChange}
                multiline
                rows={8}
                placeholder="Enter your answer here. You can use markdown-style formatting for code blocks.

Examples:

For code blocks:
```java
public class Example {
    public static void main(String[] args) {
        System.out.println('Hello, World!');
    }
}
```

For inline code: `System.out.println('Hello')`

For explanations, just write normally. The formatting will be applied automatically."
                helperText="Include code examples, explanations, or any helpful information. Use markdown-style formatting for code blocks. Click 'Preview' to see how it will look."
              />
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                  Preview of your answer:
                </Typography>
                {renderMarkdownPreview()}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={handleClose}
          sx={{ 
            px: 3, 
            py: 1.5,
            fontWeight: 600,
            borderRadius: 2
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ 
            px: 3, 
            py: 1.5,
            fontWeight: 600,
            borderRadius: 2,
            bgcolor: 'secondary.main',
            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
            '&:hover': {
              bgcolor: 'secondary.dark',
              boxShadow: '0 6px 16px rgba(124, 58, 237, 0.4)',
            }
          }}
        >
          Add Question
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddQuestionDialog; 